import "./userList.css";
import React, {useEffect, useRef, useState} from "react";
import CryptoJS from "crypto-js";
import {styled} from "@mui/material/styles";
import {useNavigate} from "react-router-dom";
import {Field, Form, Formik} from "formik";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {TextField} from "@material-ui/core";

// Material UI Components
import Box from "@mui/material/Box";
import {DataGrid} from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Avatar from "@mui/material/Avatar";
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Slide from "@mui/material/Slide";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Backdrop from "@mui/material/Backdrop";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import DialogContentText from "@mui/material/DialogContentText";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {alpha} from "@mui/material";

// Material UI Icons
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import {TransitionProps} from "@mui/material/transitions";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalculateIcon from '@mui/icons-material/Calculate';
import {
    DoneOutlined,
    MoreVert,
} from "@material-ui/icons";
import {ErrorIcon} from "react-hot-toast";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import CancelIcon from '@mui/icons-material/Cancel';
import {Card} from '@mui/material';
import {Description as DescriptionIcon, Minimize as MinimizeIcon} from '@mui/icons-material';

import {ApiDetails, KEY} from "../../dummyData";
import {Done, PaymentsOutlined} from "@mui/icons-material";

// Styled Components
const StyledDataGrid = styled(DataGrid)(({}) => ({
    "& .MuiDataGrid-columnHeaders": {
        fontSize: "0.95rem",
        fontWeight: 600,
        backgroundColor: "#f0f4f8",
        color: "#2c3e50",
        borderBottom: "2px solid #e0e0e0",
    },
    "& .MuiDataGrid-row": {
        borderBottom: "1px solid #f5f5f5",
        "&:nth-of-type(even)": {
            backgroundColor: "#fafafa",
        },
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#f5f8fa",
        transition: "background-color 0.2s ease",
    },
    "& .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.9rem",
        color: "#34495e",
        padding: "16px 8px",
    },
    "& .notice-row": {
        backgroundColor: "#fff8e1",
    },
    "& .MuiDataGrid-columnSeparator": {
        display: "none",
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: "1px solid #e0e0e0",
        backgroundColor: "#f9fafb",
    },
}));

const SearchBox = styled(Paper)(({}) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '2px 8px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
}));

const PageHeader = styled(Box)(({}) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
}));

const ActionButton = styled(Button)(({color}) => ({
    marginLeft: '8px',
    backgroundColor: color === 'primary' ? '#3498db' : color === 'error' ? '#e74c3c' : '#2ecc71',
    color: 'white',
    '&:hover': {
        backgroundColor: color === 'primary' ? '#2980b9' : color === 'error' ? '#c0392b' : '#27ae60',
    },
    borderRadius: '8px',
    textTransform: 'none',
    padding: '6px 16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

const StyledDialog = styled(Dialog)(({}) => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
    },
    '& .MuiDialogTitle-root': {
        padding: '16px 24px',
        borderBottom: '1px solid #e0e0e0',
    },
    '& .MuiDialogContent-root': {
        padding: '24px',
    },
    '& .MuiDialogActions-root': {
        padding: '12px 24px',
        borderTop: '1px solid #e0e0e0',
    },
}));

const DialogHeaderStyled = styled(DialogTitle)(({error}) => ({
    color: error ? '#e74c3c' : '#2c3e50',
    fontWeight: 600,
    fontSize: '1.25rem',
    textAlign: 'center',
}));

const FormField = styled(Box)(({}) => ({
    marginBottom: '16px',
}));

const StatusChip = styled(Chip)(({statusType}) => ({
    borderRadius: '16px',
    fontWeight: 500,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
}));

const SummaryCard = styled(Box)(({bgcolor}) => ({
    flex: 1,
    minWidth: '150px',
    p: 2,
    borderRadius: 2,
    bgcolor: bgcolor,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    }
}));

const ReportLoadingBackdrop = styled(Backdrop)(({theme}) => ({
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: alpha(theme.palette.grey[900], 0.8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.3s ease-in-out',
    '& .loading-container': {
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        padding: '24px 32px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        textAlign: 'center',
        maxWidth: '400px',
        animation: 'pulse 1.5s infinite ease-in-out',
        '@keyframes pulse': {
            '0%': {transform: 'scale(1)'},
            '50%': {transform: 'scale(1.02)'},
            '100%': {transform: 'scale(1)'},
        },
    },
    '& .progress': {
        color: theme.palette.primary.main,
        marginBottom: '16px',
    },
    '& .message': {
        color: theme.palette.text.primary,
        fontWeight: 500,
        marginBottom: '16px',
    },
    '& .cancel-button': {
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 500,
    },
}));

const styles = {
    statusChip: {
        fontWeight: 600,
        borderRadius: "16px",
    },
};

// Transition Component for Dialog
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>,
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function UserList(props) {
    const textRef = useRef();
    const navigate = useNavigate();

    // State Variables
    let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
        localStorage.getItem("myMap"),
        KEY
    );
    let portfolioDetailsDecrypted = CryptoJS.AES.decrypt(
        localStorage.portfolioDetails,
        KEY
    );
    let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);

    // Loading State
    const [backDrop, setBackDrop] = useState(
        <Backdrop
            sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}}
            open={true}
            onClick={null}
        >
            <CircularProgress color="inherit"/>
        </Backdrop>
    );

    // Data States
    let [tenants, updateTenants] = React.useState([]);
    const [searchResults, setSearchResults] = useState(tenants);
    const [filteredResults, setFilteredResults] = useState(tenants);
    let [properties, updateProperties] = React.useState([]);
    const [dateFrom, setDateFrom] = React.useState(new Date());
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("ALL");
    const [reportType, setReportType] = useState("ALL");

    // Modal States
    const [deleteModal, setOpen] = useState(false);
    const [transactionModal, setTransactionModal] = useState(false);
    const [arrearsModal, setArrearsModal] = useState(false);
    const [addTenantModal, setAddTenantModal] = useState(false);
    const [depositModal, setDepositModal] = useState(false);
    const [reportModal, setReportModal] = useState(false);
    const [reportLoading, setReportLoading] = useState(false); // New state for report loading UI

    // Menu States
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const openMenu = Boolean(menuAnchorEl);

    // Deposit States
    const [refundableDeposit, setRefundableDeposit] = useState(0);
    const [totalDeposit, setTotalDeposit] = useState(0);
    const [depositSet, setDepositSet] = useState(false);
    const [expensesIncurred, setExpensesIncurred] = useState(1);
    let [depositList, setDepositList] = useState([new Map()]);
    const [expenseItem] = useState(new Map());

    // Tenant Info State
    let [tenantInfo, setTenantInfo] = React.useState({
        tenant_id: "",
        name: "",
        last_name: "",
        unit: "",
        property_id: "",
    });

    // Loading States
    const [loading, setLoading] = React.useState(false);
    const [transactionLoading, setTransactionLoading] = React.useState(false);
    const [refreshing, setRefreshing] = React.useState(false);
    const [generatingReport, setGeneratingReport] = React.useState(false);

    // Form Initial Values
    let initialValues4 = {
        id: "",
        first_name: "",
        date: dateFrom,
        amount: "",
    };

    // Report form values
    const [reportFormValues, setReportFormValues] = useState({
        includeDetails: true,
        groupBy: "property",
        format: "pdf"
    });

    // Input Fields State
    const [inputs, setInputs] = useState(
        <div>
            <br/>
            <div
                style={{
                    display: "none",
                    justifyContent: "space-between",
                }}
            >
                <TextField
                    label="Description"
                    type={"text"}
                    variant="outlined"
                    required
                    style={{marginTop: "10px", width: "49%"}}
                />
                <TextField
                    label="Amount"
                    type={"number"}
                    variant="outlined"
                    style={{marginTop: "10px", width: "49%"}}
                />
            </div>
        </div>
    );

    // UseEffect to Fetch Data
    useEffect(() => {
        props.refreshSideBar();
        fetchTenants();
    }, []);

    // Set filtered results whenever searchResults or filterStatus changes
    useEffect(() => {
        if (filterStatus === "ALL") {
            setFilteredResults(searchResults);
        } else if (filterStatus === "PAID") {
            setFilteredResults(searchResults.filter(tenant => tenant.status === "COMPLETED"));
        } else if (filterStatus === "PARTIAL") {
            setFilteredResults(searchResults.filter(tenant => tenant.status !== "COMPLETED"));
        }
    }, [searchResults, filterStatus]);

    // Fetch all tenants function
    const fetchTenants = () => {
        setRefreshing(true);
        fetch(ApiDetails + "pegasus/visionary/tenant/get/all/tenants", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({
                landlord_id: JSON.parse(originalText)[0][1],
            }),
        })
            .then(async (response) => {
                let tenants = await response.json();
                if (response.status === 200) {
                    fetch(ApiDetails + "pegasus/visionary/property/get/all/properties", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                        },
                        body: JSON.stringify({
                            landlord_id: JSON.parse(originalText)[0][1],
                        }),
                    })
                        .then(async (response) => {
                            let data = await response.json();
                            if (response.status === 200) {
                                updateProperties(data);
                                updateTenants(tenants);
                                setSearchResults(tenants);
                                setFilteredResults(tenants);
                                setBackDrop(<></>);
                                setRefreshing(false);
                            } else {
                                props.snackBar({
                                    text: "Failed Fetching Property Details",
                                    type: "error",
                                });
                                setRefreshing(false);
                            }
                        })
                        .catch((err) => {
                            console.log(err);
                            setRefreshing(false);
                        });
                } else {
                    props.snackBar({text: "Failed Fetching Tenant Details", type: "error"});
                    setRefreshing(false);
                }
            })
            .catch((err) => {
                console.log(err);
                setRefreshing(false);
            });
    };

    // Helper Functions
    function stringToColor(string) {
        let hash = 0;
        let i;
        for (i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = '#';
        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        return color;
    }

    function stringAvatar(name) {
        return {
            sx: {
                bgcolor: stringToColor(name),
            },
            children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
        };
    }

    // Handle Modal Close
    const handleClose = () => {
        setOpen(false);
        setArrearsModal(false);
        setDepositModal(false);
        setAddTenantModal(false);
        setTransactionModal(false);
        setReportModal(false);
        setMenuAnchorEl(null);
    };

    // Handle Menu Open
    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    // Calculate Refundable Deposit
    const calculateRefundableDeposit = () => {
        try {
            expenseItem.forEach((value, key) => {
                let array = key.split(" ");
                let uniqueString = array[array.length - 1];
                if (expenseItem.has("refundable " + uniqueString)) {
                    let refundableValue = expenseItem.get("refundable " + uniqueString);
                    if (!(refundableValue.toString().trim().toUpperCase() === "YES" || refundableValue.toString().trim().toUpperCase() === "NO")) {
                        throw new Error("Fill in either YES OR NO for refundable")
                    }
                } else {
                    throw new Error("Fill in if refundable for all entries")
                }
            });
        } catch (e) {
            props.snackBar({
                text: e.message,
                type: "error",
            });
            return;
        }
        let depositAmount = 0;
        expenseItem.forEach((value, key) => {
            if (key.toString().includes("value")) {
                depositAmount += parseFloat(value);
            }
        });
        setTotalDeposit(depositAmount)
        expenseItem.set("Total deposit", depositAmount)
        let refundableDepositAmount = 0;
        let handledKeys = [];
        expenseItem.forEach((value, key) => {
            let array = key.split(" ");
            let uniqueString = array[array.length - 1];
            if (handledKeys.includes(uniqueString)) {
                return
            }
            if (expenseItem.has("refundable " + uniqueString)) {
                let refundableValue = expenseItem.get("refundable " + uniqueString);
                if (refundableValue.toString().toUpperCase().trim() === "YES") {
                    refundableDepositAmount += parseFloat(expenseItem.get("value for description " + uniqueString))
                }
            }
            handledKeys.push(uniqueString)
        });
        setRefundableDeposit(refundableDepositAmount)
        expenseItem.set("Refundable deposit", refundableDepositAmount)
        setDepositList(expenseItem)
        setDepositSet(true)
    };

    // Handle Input Change
    const handleChange = () => (event) => {
        expenseItem.set(event.target.name, event.target.value);
        setDepositSet(false)
    };

    // Handle Delete
    const handleDelete = () => {
        setLoading(true);
        let tenantPropertyMap = properties.filter(
            (property) =>
                new Map(Object.entries(property)).get("property_id") ===
                tenantInfo.property_id
        );
        let tenantPropertyName = new Map(Object.entries(tenantPropertyMap[0])).get(
            "property_name"
        );
        fetch(ApiDetails + "pegasus/visionary/tenant/delete", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({
                ...tenantInfo,
                property_name: tenantPropertyName,
                landlord_email_address: JSON.parse(originalText)[5][1],
            }),
        })
            .then(async (response) => {
                if (response.status === 200) {
                    fetchTenants();
                    props.snackBar({
                        text: "Tenant Deleted Successfully",
                        type: "success",
                    });
                    handleClose();
                } else if (response.status === 500) {
                    props.snackBar({
                        text: "Something went wrong. Please try again later.",
                        type: "error",
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                props.snackBar({
                    text: "Something Went Wrong. Try Again Later",
                    type: "error",
                });
            });
    };

    // Add Transaction
    const addTransaction = (values) => {
        setTransactionLoading(true);
        let body = {
            TransactionType: "Pay Bill",
            TransID: "**********",
            TransTime: dateFrom,
            TransAmount: values.amount,
            BusinessShortCode: "4103275",
            BillRefNumber: tenantInfo.account_number,
            InvoiceNumber: "",
            OrgAccountBalance: "170.00",
            ThirdPartyTransID: "",
            MSISDN: "2547 ***** 249",
            FirstName: tenantInfo.name,
            tenant_id: tenantInfo.id,
            landlord_id: JSON.parse(originalText)[0][1],
        };
        if (values.amount === "") {
            props.snackBar({text: "Amount Can't Be Empty", type: "error"});
            setTransactionLoading(false);
            return;
        }
        fetch(ApiDetails + "pegasus/visionary/payments/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify(body),
        })
            .then(async (response) => {
                if (response.status === 200) {
                    props.snackBar({
                        text: "Transaction Added Successfully",
                        type: "success",
                    });
                    fetchTenants();
                    handleClose();
                } else {
                    handleClose();
                    props.snackBar({
                        text: "Something Went Wrong, Please Try Again Later",
                        type: "error",
                    });
                }
                setTransactionLoading(false);
            })
            .catch((err) => {
                console.log(err);
                handleClose();
                props.snackBar({
                    text: "Something Went Wrong, Please Try Again Later",
                    type: "error",
                });
                setTransactionLoading(false);
            });
    };

    // Handle Search
    const handleSearch = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);
        if (value === '') {
            setSearchResults(tenants);
            return;
        }
        const filtered = tenants.filter(tenant =>
            tenant.first_name.toLowerCase().includes(value) ||
            tenant.last_name.toLowerCase().includes(value) ||
            tenant.house_number.toLowerCase().includes(value) ||
            tenant.phone_number.includes(value)
        );
        setSearchResults(filtered);
    };

    // Generate Report
    const generateReport = async (status) => {
        setGeneratingReport(true);
        setReportLoading(true); // Show loading UI
        handleClose(); // Close report modal

        const controller = new AbortController(); // For canceling the fetch
        const signal = controller.signal;

        // Store controller to allow cancellation
        const abortControllerRef = {current: controller};

        // Map reportType to API endpoint
        const endpointMap = {
            PAID: 'get/property/cleared/tenants/bill',
            PARTIAL: 'get/property/pending/tenants/bill'
        };

        const reportUrl = endpointMap[status] || 'get/property/pending/tenants/bill'; // Fallback to pending

        let data = {
            landlord_id: JSON.parse(originalText)[0][1],
            /*group_by: reportFormValues.groupBy,
            include_details: reportFormValues.includeDetails,
            format: reportFormValues.format === 'pdf' ? 'pdf' : 'excel', // Respect format*/
        };

        // Add date range if custom
        if (reportFormValues.dateRange === 'custom') {
            data.from_date = dateFrom.toISOString().split('T')[0];
            data.to_date = dateFrom.toISOString().split('T')[0]; // Note: to_date is same as from_date in original code
        }

        try {
            const response = await fetch(ApiDetails + "pegasus/visionary/reports/" + reportUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify(data),
                signal,
            });

            // Extract filename from headers
            let fileName = reportType === 'PAID' ? "paid_tenants_report.xlsx" : "pending_tenants_report.xlsx";
            const contentDisposition = response.headers.get("Content-Disposition");
            if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                    fileName = match[1].replace(/['"]/g, '');
                }
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.Message || `HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
                console.warn("Unexpected content type:", contentType);
            }

            const blob = await response.blob();
            if (blob.size === 0) {
                throw new Error("Downloaded file is empty");
            }

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            link.style.display = "none";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            props.snackBar({
                text: `Successfully downloaded: ${fileName}`,
                type: "success",
            });
        } catch (error) {
            if (error.name === 'AbortError') {
                props.snackBar({
                    text: "Report generation canceled",
                    type: "info",
                });
                return;
            }
            console.error("Download error:", error);
            let errorMessage = "Failed to download report. Please try again.";
            if (error.message.includes("404")) {
                errorMessage = "No data found for the selected criteria";
            } else if (error.message.includes("400")) {
                errorMessage = "Invalid request. Please check your parameters.";
            } else if (error.message.includes("Network")) {
                errorMessage = "Network error. Please check your connection.";
            }
            props.snackBar({
                text: errorMessage,
                type: "error",
            });
        } finally {
            setGeneratingReport(false);
            setReportLoading(false); // Hide loading UI
            abortControllerRef.current = null; // Clear controller
        }

        // Return cleanup function for cancel
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    };

    // Handle Cancel Report Generation
    const handleCancelReport = () => {
        if (window.confirm("Are you sure you want to cancel report generation?")) {
            setReportLoading(false);
            setGeneratingReport(false);
            // The abort is handled within generateReport's cleanup
            props.snackBar({
                text: "Report generation canceled",
                type: "info",
            });
        }
    };

    // Handle filter by status
    const handleFilterByStatus = (status) => {
        setFilterStatus(status);
        setReportType(status);
    };

    // Show report options menu
    const showReportOptions = (status) => {
        setReportType(status);
        setReportModal(true);
    };

    // Data Grid Columns
    let columns = [
        {
            field: "user",
            headerName: "Tenant",
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "3px",
                        backgroundColor: params.row.state === "NOTICE" ? "#fff8e1" : "",
                    }}
                    className="userListUser"
                >
                    <Tooltip title={params.row.state === "NOTICE" ? "Notice" : "Active"}>
                        <div style={{margin: "0 12px 0 0"}}>
                            <Avatar
                                {...stringAvatar(params.row.first_name.toString() + " " + params.row.last_name.toString())}
                                style={{width: 40, height: 40, fontSize: "16px", fontWeight: "bold"}}
                            />
                        </div>
                    </Tooltip>
                    <div>
                        <Typography variant="body1" style={{fontWeight: 500}}>
                            {params.row.first_name} {params.row.last_name}
                        </Typography>
                        <Typography variant="caption" color="primary">
                            {params.row.phone_number}
                        </Typography>
                    </div>
                </div>
            ),
        },
        {
            field: "house_number",
            headerName: "House No.",
            minWidth: 120,
            flex: 0,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    style={{
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                        fontWeight: 500,
                    }}
                />
            ),
        },
        {
            field: "bill",
            headerName: "Wallet",
            minWidth: 100,
            flex: 0.8,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Typography
                    style={{
                        color: params.row.status === "PENDING" ? "#D32F2F" : "#388E3C",
                        fontWeight: 600,
                    }}
                >
                    {params.row.Wallet.toLocaleString("en-US", {
                        style: "currency",
                        currency: "KSH",
                    })}
                </Typography>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                switch (params.row.status) {
                    case "PENDING":
                        return <Chip label="PENDING" color="error" variant="outlined" sx={styles.statusChip}/>;
                    case "COMPLETED":
                        return <Chip label="PAID" color="success" variant="outlined" icon={<Done/>}
                                     sx={styles.statusChip}/>;
                    default:
                        return <Chip label="PARTIAL" color="warning" variant="outlined" sx={styles.statusChip}/>;
                }
            },
        },
        {
            field: "login_successful",
            headerName: "App Access",
            flex: 0.8,
            minWidth: 100,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <div className="userListAction">
                    {params.row.login_successful ? (
                        <StatusChip
                            label="Active"
                            color="success"
                            size="small"
                            variant="outlined"
                            statusType="success"
                            icon={<DoneOutlined style={{fontSize: 16}}/>}
                        />
                    ) : (
                        <StatusChip
                            label="Inactive"
                            color="error"
                            size="small"
                            variant="outlined"
                            statusType="error"
                            icon={<ErrorIcon style={{fontSize: 16}}/>}
                        />
                    )}
                </div>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 250,
            flex: 1.2,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <>
                    {params.row.agency_id !== 0 && params.row.agency_id !== JSON.parse(originalText)[0][1] ? (
                        <></>
                    ) : (
                        <Box sx={{display: "flex", gap: 1}}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<PaymentsOutlined/>}
                                onClick={() => {
                                    setTenantInfo({
                                        id: params.row.tenant_id,
                                        name: params.row.first_name,
                                        last_name: params.row.last_name,
                                        account_number: params.row.account_number,
                                    });
                                    setTransactionModal(true);
                                }}
                                style={{borderRadius: 20}}
                            >
                                Pay
                            </Button>
                            <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<MoreVert/>}
                                onClick={() => {
                                    navigate("/tenant/" + params.row.tenant_id, {
                                        state: {
                                            tenant_id: params.row.tenant_id,
                                        },
                                    });
                                }}
                                style={{borderRadius: 20}}
                            >
                                Details
                            </Button>
                        </Box>
                    )}
                </>
            ),
        }
    ];

    return (
        <>
            {/* Report Loading UI */}
            <ReportLoadingBackdrop
                open={reportLoading}
                sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(4px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 1300
                }}
                aria-label="Report generation in progress"
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        padding: 3,
                        position: 'relative'
                    }}
                >
                    {/* Main Loading Card */}
                    <Card
                        elevation={24}
                        sx={{
                            borderRadius: 3,
                            padding: 4,
                            minWidth: 320,
                            maxWidth: 480,
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 100%)',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 20px rgba(0, 0, 0, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.8)',
                            position: 'relative',
                            overflow: 'visible',
                            transform: 'translateY(0)',
                            animation: 'slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            '@keyframes slideInUp': {
                                '0%': {
                                    transform: 'translateY(40px)',
                                    opacity: 0
                                },
                                '100%': {
                                    transform: 'translateY(0)',
                                    opacity: 1
                                }
                            }
                        }}
                    >
                        {/* Progress Indicator */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mb: 3,
                                position: 'relative'
                            }}
                        >
                            {/* Outer Ring */}
                            <CircularProgress
                                size={80}
                                thickness={2}
                                sx={{
                                    color: 'rgba(46, 125, 50, 0.2)',
                                    position: 'absolute'
                                }}
                                variant="determinate"
                                value={100}
                            />

                            {/* Main Progress */}
                            <CircularProgress
                                size={80}
                                thickness={4}
                                sx={{
                                    color: '#2e7d32',
                                    filter: 'drop-shadow(0 2px 4px rgba(46, 125, 50, 0.3))',
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    }
                                }}
                            />

                            {/* Center Icon */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: 'rgba(46, 125, 50, 0.1)',
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    animation: 'pulse 2s infinite ease-in-out',
                                    '@keyframes pulse': {
                                        '0%, 100%': {
                                            transform: 'translate(-50%, -50%) scale(1)',
                                            opacity: 1
                                        },
                                        '50%': {
                                            transform: 'translate(-50%, -50%) scale(1.1)',
                                            opacity: 0.8
                                        }
                                    }
                                }}
                            >
                                <DescriptionIcon
                                    sx={{
                                        color: '#2e7d32',
                                        fontSize: 20
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Main Content */}
                        <Box sx={{textAlign: 'center', mb: 3}}>
                            <Typography
                                variant="h5"
                                fontWeight="600"
                                sx={{
                                    mb: 1,
                                    color: '#1a1a1a',
                                    letterSpacing: '-0.5px'
                                }}
                            >
                                Generating Report
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#666',
                                    mb: 2,
                                    lineHeight: 1.6
                                }}
                            >
                                Creating your {reportType === 'PAID' ? 'Paid' : 'Pending'} tenants report
                            </Typography>

                            {/* Progress Steps */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 1,
                                mb: 3
                            }}>
                                {[
                                    'Fetching data',
                                    'Processing records',
                                    'Formatting report',
                                    'Finalizing'
                                ].map((step, index) => (
                                    <Box
                                        key={step}
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: '#2e7d32',
                                            opacity: 0.3,
                                            animation: `stepPulse 2s infinite ${index * 0.5}s`,
                                            '@keyframes stepPulse': {
                                                '0%, 80%, 100%': {
                                                    opacity: 0.3,
                                                    transform: 'scale(1)'
                                                },
                                                '40%': {
                                                    opacity: 1,
                                                    transform: 'scale(1.2)'
                                                }
                                            }
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Estimated Time */}
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#999',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                This usually takes 30-60 seconds
                            </Typography>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            pt: 2,
                            borderTop: '1px solid rgba(0, 0, 0, 0.08)'
                        }}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon/>}
                                onClick={handleCancelReport}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#666',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: '#d32f2f',
                                        color: '#d32f2f',
                                        backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(211, 47, 47, 0.15)'
                                    }
                                }}
                                aria-label="Cancel report generation"
                            >
                                Cancel
                            </Button>

                           {/* <Button
                                variant="text"
                                startIcon={<MinimizeIcon/>}
                                onClick={() => {
                                    // Add minimize functionality if needed
                                    console.log('Minimize to background');
                                }}
                                sx={{
                                    color: '#666',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        color: '#333'
                                    }
                                }}
                            >
                                Minimize
                            </Button>*/}
                        </Box>
                    </Card>

                    {/* Background Elements for Visual Interest */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '20%',
                            left: '10%',
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            background: 'linear-gradient(45deg, rgba(46, 125, 50, 0.1), rgba(46, 125, 50, 0.05))',
                            animation: 'float 6s ease-in-out infinite',
                            '@keyframes float': {
                                '0%, 100%': {transform: 'translateY(0px)'},
                                '50%': {transform: 'translateY(-20px)'}
                            }
                        }}
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '20%',
                            right: '15%',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
                            animation: 'float 8s ease-in-out infinite reverse',
                        }}
                    />
                </Box>
            </ReportLoadingBackdrop>

            {/* Delete Modal */}
            <StyledDialog
                open={deleteModal}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogHeaderStyled error>Delete Tenant</DialogHeaderStyled>
                <DialogContent>
                    <Box sx={{textAlign: 'center', mb: 2}}>
                        <Avatar
                            {...stringAvatar(tenantInfo.name + ' ' + tenantInfo.last_name)}
                            style={{width: 64, height: 64, margin: '0 auto 16px', fontSize: 24}}
                        />
                        <Typography variant="h6">{tenantInfo.name} {tenantInfo.last_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                            House No: {tenantInfo.unit}
                        </Typography>
                    </Box>
                    <DialogContentText id="alert-dialog-slide-description">
                        <Typography paragraph>
                            Deleting a tenant will completely remove them and their respective
                            documents from the system and mark their house as vacant.
                        </Typography>
                        <Typography variant="subtitle1" color="error" sx={{fontWeight: 500, mt: 2}}>
                            Are you sure you want to delete this tenant?
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        loading={loading}
                        startIcon={<DeleteIcon/>}
                        loadingPosition="start"
                    >
                        Delete Tenant
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>

            {/* Transaction Modal */}
            <StyledDialog
                open={transactionModal}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogHeaderStyled>
                    Record Payment
                </DialogHeaderStyled>
                <DialogContent>
                    <Box sx={{textAlign: 'center', mb: 3}}>
                        <Avatar
                            {...stringAvatar(tenantInfo.name + ' ' + tenantInfo.last_name)}
                            style={{width: 64, height: 64, margin: '0 auto 16px', fontSize: 24}}
                        />
                        <Typography variant="h6">{tenantInfo.name} {tenantInfo.last_name}</Typography>
                    </Box>
                    <Formik initialValues={initialValues4} onSubmit={addTransaction}>
                        {() => (
                            <Form noValidate>
                               {/* <FormField>
                                    <Field
                                        as={TextField}
                                        name="first_name"
                                        label="Tenant Name"
                                        value={tenantInfo.name + " " + tenantInfo.last_name}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        disabled={true}
                                    />
                                </FormField>*/}
                                <FormField>
                                    <Field
                                        as={TextField}
                                        name="amount"
                                        label="Amount"
                                        type="number"
                                        fullWidth
                                        required
                                        variant="outlined"
                                        helperText="Enter the payment amount"
                                    />
                                </FormField>
                                <FormField>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            disableFuture
                                            label="Payment Date"
                                            openTo="day"
                                            views={["year", "month", "day"]}
                                            value={dateFrom}
                                            onChange={(newValue) => {
                                                setDateFrom(newValue);
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth/>}
                                        />
                                    </LocalizationProvider>
                                </FormField>
                                <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 3}}>
                                    <Button
                                        onClick={handleClose}
                                        variant="outlined"
                                        color="inherit"
                                        sx={{mr: 2}}
                                    >
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        loading={transactionLoading}
                                        startIcon={<SendIcon/>}
                                        loadingPosition="start"
                                    >
                                        Record Payment
                                    </LoadingButton>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </StyledDialog>

            {/* Report Modal */}
            <StyledDialog
                open={reportModal}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogHeaderStyled>
                    Generate {reportType} Tenant Report
                </DialogHeaderStyled>
                <DialogContent>
                    <Box sx={{mb: 3}}>
                        <Typography variant="body1" sx={{mb: 2}}>
                            Configure your report options below:
                        </Typography>
                        {reportFormValues.dateRange === "custom" && (
                            <Box sx={{display: 'flex', gap: 2, mt: 2}}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="From Date"
                                        value={dateFrom}
                                        onChange={(newValue) => setDateFrom(newValue)}
                                        renderInput={(params) => <TextField {...params} fullWidth/>}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="To Date"
                                        value={dateFrom}
                                        onChange={(newValue) => setDateFrom(newValue)}
                                        renderInput={(params) => <TextField {...params} fullWidth/>}
                                    />
                                </LocalizationProvider>
                            </Box>
                        )}
                        <FormField sx={{mt: 3}}>
                            <Typography variant="subtitle2" sx={{mb: 1}}>Group By</Typography>
                            <Box sx={{display: 'flex', gap: 1}}>
                                <Button
                                    variant={reportFormValues.groupBy === "property" ? "contained" : "outlined"}
                                    onClick={() => setReportFormValues({...reportFormValues, groupBy: "property"})}
                                    sx={{flex: 1}}
                                >
                                    Property
                                </Button>
                                <Button
                                    variant={reportFormValues.groupBy === "tenant" ? "contained" : "outlined"}
                                    onClick={() => setReportFormValues({...reportFormValues, groupBy: "tenant"})}
                                    sx={{flex: 1}}
                                >
                                    Tenant
                                </Button>
                                <Button
                                    variant={reportFormValues.groupBy === "status" ? "contained" : "outlined"}
                                    onClick={() => setReportFormValues({...reportFormValues, groupBy: "status"})}
                                    sx={{flex: 1}}
                                >
                                    Status
                                </Button>
                            </Box>
                        </FormField>
                        <FormField sx={{mt: 3}}>
                            <Typography variant="subtitle2" sx={{mb: 1}}>Include Details</Typography>
                            <Box sx={{display: 'flex', gap: 1}}>
                                <Button
                                    variant={reportFormValues.includeDetails ? "contained" : "outlined"}
                                    onClick={() => setReportFormValues({...reportFormValues, includeDetails: true})}
                                    sx={{flex: 1}}
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant={!reportFormValues.includeDetails ? "contained" : "outlined"}
                                    onClick={() => setReportFormValues({...reportFormValues, includeDetails: false})}
                                    sx={{flex: 1}}
                                >
                                    No
                                </Button>
                            </Box>
                        </FormField>
                        <FormField sx={{mt: 3}}>
                            <Typography variant="subtitle2" sx={{mb: 1}}>Format</Typography>
                            <Box sx={{display: 'flex', gap: 1}}>
                                <Button
                                    variant={reportFormValues.format === "pdf" ? "contained" : "outlined"}
                                    onClick={() => setReportFormValues({...reportFormValues, format: "pdf"})}
                                    startIcon={<PictureAsPdfIcon/>}
                                    sx={{flex: 1}}
                                >
                                    PDF
                                </Button>
                                <Button
                                    variant={reportFormValues.format === "excel" ? "contained" : "outlined"}
                                    onClick={() => setReportFormValues({...reportFormValues, format: "excel"})}
                                    startIcon={<FileDownloadIcon/>}
                                    sx={{flex: 1}}
                                >
                                    Excel
                                </Button>
                            </Box>
                        </FormField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={generateReport}
                        loading={generatingReport}
                        startIcon={<FileDownloadIcon/>}
                        loadingPosition="start"
                    >
                        Generate Report
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>

            {/* Deposit Modal */}
            <StyledDialog
                open={depositModal}
                fullWidth
                maxWidth="md"
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
            >
                <DialogHeaderStyled>Tenant Deposit Details</DialogHeaderStyled>
                <DialogContent>
                    <Box sx={{mb: 3}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                            <Typography variant="h6" color="primary">Deposit Details</Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddCircleIcon/>}
                                onClick={() => {
                                    setExpensesIncurred(expensesIncurred + 1);
                                    setInputs(
                                        [...Array(expensesIncurred)].map(
                                            (elementInArray, index) => (
                                                <div key={index}>
                                                    <Box sx={{display: 'flex', gap: 2, mb: 2}}>
                                                        <TextField
                                                            label="Description"
                                                            type="text"
                                                            variant="outlined"
                                                            name={"description " + (index + 1)}
                                                            required
                                                            inputRef={textRef}
                                                            fullWidth
                                                            onChange={handleChange()}
                                                        />
                                                        <TextField
                                                            label="Amount"
                                                            type="number"
                                                            name={"value for description " + (index + 1)}
                                                            variant="outlined"
                                                            fullWidth
                                                            onChange={handleChange()}
                                                        />
                                                        <TextField
                                                            label="Refundable (Yes/No)"
                                                            type="text"
                                                            name={"refundable " + (index + 1)}
                                                            variant="outlined"
                                                            onChange={handleChange()}
                                                            style={{width: '30%'}}
                                                        />
                                                    </Box>
                                                </div>
                                            )
                                        )
                                    );
                                }}
                            >
                                Add Deposit Item
                            </Button>
                        </Box>
                        <Divider sx={{mb: 3}}/>
                        <Box>{inputs}</Box>
                        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CalculateIcon/>}
                                onClick={calculateRefundableDeposit}
                            >
                                Calculate
                            </Button>
                        </Box>
                        <Box sx={{mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 2}}>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                                <Typography variant="subtitle1" color="primary">
                                    Total Deposit:
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                    {totalDeposit.toLocaleString("en-US", {
                                        style: "currency",
                                        currency: "KSH",
                                    })}
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant="subtitle1" color="primary">
                                    Refundable Deposit:
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                    {refundableDeposit.toLocaleString("en-US", {
                                        style: "currency",
                                        currency: "KSH",
                                    })}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                            handleClose();
                            setAddTenantModal(true);
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            handleClose();
                            setAddTenantModal(true);
                        }}
                        disabled={!depositSet}
                        loading={false}
                        startIcon={<SendIcon/>}
                        loadingPosition="start"
                    >
                        Confirm & Continue
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>

            {/* Report Options Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={openMenu}
                onClose={() => setMenuAnchorEl(null)}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        borderRadius: 2,
                        minWidth: 180,
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
                    },
                }}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            >
                <MenuItem onClick={() => {
                    setMenuAnchorEl(null);
                    showReportOptions(reportType);
                }}>
                    <ListItemIcon>
                        <FileDownloadIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Export Excel</ListItemText>
                </MenuItem>
            </Menu>

            {/* Main Content */}
            <Box sx={{
                p: 3,
                bgcolor: '#ffffff',
                borderRadius: 2,
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
            }}
                 style={{overflowY: "scroll"}}
            >
                {/* Page Header */}
                <PageHeader>
                    <Typography variant="h5" fontWeight="600" color="primary">
                        Tenant Management
                    </Typography>
                    <Box sx={{display: 'flex'}}>
                        <ActionButton
                            color="primary"
                            startIcon={<RefreshIcon/>}
                            onClick={fetchTenants}
                            disabled={refreshing}
                        >
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </ActionButton>
                    </Box>
                </PageHeader>

                {/* Search and Filter Bar */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3
                }}>
                    <SearchBox elevation={0}>
                        <SearchIcon sx={{color: 'action.active', mr: 1, ml: 1}}/>
                        <InputBase
                            placeholder="Search tenants by name, house number or phone..."
                            fullWidth
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </SearchBox>
                </Box>

                {/* Stats Summary */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    flexWrap: 'wrap'
                }}>
                    <Box
                        component={SummaryCard}
                        onClick={() => handleFilterByStatus("ALL")}
                        bgcolor={filterStatus === "ALL" ? '#d0e3ff' : '#e3f2fd'}
                        sx={{
                            border: filterStatus === "ALL" ? '2px solid #1976d2' : 'none',
                            cursor: 'pointer',
                        }}
                    >
                        <Typography variant="h4" fontWeight="bold" color="primary">
                            {searchResults.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Total Tenants
                        </Typography>
                    </Box>
                    <Box
                        component={SummaryCard}
                        onClick={() => handleFilterByStatus("PAID")}
                        bgcolor={filterStatus === "PAID" ? '#c8e6c9' : '#e8f5e9'}
                        sx={{
                            border: filterStatus === "PAID" ? '2px solid #388e3c' : 'none',
                            cursor: 'pointer',
                        }}
                    >
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                            {searchResults.filter(tenant => tenant.status === "COMPLETED").length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Paid
                        </Typography>
                        {filterStatus === "PAID" && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<FileDownloadIcon/>}
                                // onClick={handleMenuClick}
                                onClick={() => {
                                    generateReport("PAID")
                                }}
                                sx={{mt: 1}}
                            >
                                Report
                            </Button>
                        )}
                    </Box>
                    <Box
                        component={SummaryCard}
                        onClick={() => handleFilterByStatus("PARTIAL")}
                        bgcolor={filterStatus === "PARTIAL" ? '#ffecb3' : '#fff8e1'}
                        sx={{
                            border: filterStatus === "PARTIAL" ? '2px solid #f57c00' : 'none',
                            cursor: 'pointer',
                        }}
                    >
                        <Typography variant="h4" fontWeight="bold" color="warning.main">
                            {searchResults.filter(tenant => tenant.status !== "COMPLETED").length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Partial/Pending
                        </Typography>
                        {filterStatus === "PARTIAL" && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<FileDownloadIcon/>}
                                // onClick={handleMenuClick}
                                onClick={() => {
                                    generateReport("PARTIAL")
                                }}
                                sx={{mt: 1}}
                            >
                                Report
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Tenants Table */}
                <Box sx={{height: 'calc(100vh - 300px)', width: '100%'}}>
                    <StyledDataGrid
                        initialState={{
                            sorting: {
                                sortModel: [{field: "house_number", sort: "asc"}],
                            },
                            pagination: {
                                pageSize: 10,
                            },
                        }}
                        rows={filteredResults}
                        columns={columns}
                        disableSelectionOnClick
                        rowsPerPageOptions={[5, 10, 25]}
                        getRowClassName={(params) =>
                            params.row.state === 'NOTICE' ? 'notice-row' : ''
                        }
                        autoHeight
                        loading={refreshing}
                        pagination
                        sx={{
                            '& .MuiDataGrid-virtualScroller': {
                                overflow: 'auto',
                            },
                            border: 'none',
                        }}
                    />
                </Box>
            </Box>
        </>
    );
}